/* Filters select an actual native variant, never a Cartesian combination of options. */
(() => {
  class OnaSourceFilter extends HTMLElement {
    async connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const {signal} = this.controller;
      await customElements.whenDefined('ona-product-card');
      if (!this.isConnected || signal.aborted) return;
      this.cards = [...this.querySelectorAll('ona-product-card')];
      this.controls = Object.fromEntries([...this.querySelectorAll('[data-source-filter]')].map(control => [control.dataset.sourceFilter, control]));
      for (const field of ['color', 'size']) {
        const values = new Set();
        this.cards.forEach(card => card.data?.variants.forEach(variant => values.add(variant.options[card.data[`${field}Index`]])));
        values.forEach(value => { if (!value) return; const option = document.createElement('option'); option.value = value; option.textContent = value; this.controls[field].append(option); });
      }
      this.querySelector('.ona-source-filter-controls').hidden = false;
      Object.values(this.controls).forEach(control => control.addEventListener('change', () => {
        this.sizeReset = false;
        if (control === this.controls.format && control.value !== 'all' && this.controls.size.value) {
          const compatible = this.cards.some(card => card.dataset.sourcePack === control.value && card.data?.variants.some(variant => variant.options[card.data.sizeIndex] === this.controls.size.value));
          if (!compatible) { this.controls.size.value = ''; this.sizeReset = true; }
        }
        this.apply(true);
      }, {signal}));
      this.addEventListener('ona:color-select', event => { if (!this.controls.color.value) return; this.controls.color.value = event.detail.color; this.apply(true); }, {signal});
      this.querySelector('[data-source-reset]').addEventListener('click', () => { Object.entries(this.controls).forEach(([field, control]) => {control.value = field === 'format' ? '2' : '';}); this.sizeReset = false; this.apply(true, true); }, {signal});
      window.addEventListener('popstate', () => this.fromURL(), {signal});
      this.fromURL();
    }
    fromURL() {
      const params = new URL(location.href).searchParams;
      Object.entries(this.controls).forEach(([field, control]) => {const fallback = field === 'format' ? '2' : ''; const value = params.get(`source_${field}`) || fallback; control.value = [...control.options].some(option => option.value === value) ? value : fallback;});
      this.apply(false);
    }
    apply(writeURL, reset = false) {
      const color = this.controls.color.value, size = this.controls.size.value, format = this.controls.format.value;
      let count = 0;
      this.cards.forEach(card => {
        const data = card.data; if (!data) return;
        const matches = data.variants.filter(variant => (!color || variant.options[data.colorIndex] === color) && (!size || variant.options[data.sizeIndex] === size));
        const shown = (format === 'all' || card.dataset.sourcePack === format) && matches.length > 0;
        card.hidden = !shown; if (!shown) return; count++;
        const variant = (!reset && matches.find(item => String(item.id) === String(card.variant?.id))) || (!reset && matches.find(item => item.available && item.options[data.colorIndex] === card.variant?.options[data.colorIndex])) || matches.find(item => String(item.id) === String(data.selected)) || matches.find(item => item.available) || matches[0];
        if (variant) card.update(variant);
      });
      this.querySelector('[data-source-count]').textContent = `${count} ${count === 1 ? 'format' : 'formats'}${this.sizeReset ? ' · Size reset for this format' : ''}`;
      this.querySelector('[data-source-empty]').hidden = count !== 0;
      this.querySelector('[data-source-reset]').disabled = !color && !size && format === '2';
      this.dataset.onaReady = 'true';
      if (writeURL) {
        const url = new URL(location.href);
        Object.entries(this.controls).forEach(([field, control]) => {if(control.value && !(field === 'format' && control.value === '2')) url.searchParams.set(`source_${field}`,control.value); else url.searchParams.delete(`source_${field}`);});
        history.pushState({}, '', url.pathname + url.search + url.hash);
      }
    }
    disconnectedCallback() {this.controller?.abort(); this.controller = null;}
  }
  if (!customElements.get('ona-source-filter')) customElements.define('ona-source-filter', OnaSourceFilter);
})();

/* Format switches are native product navigation; quantity remains one purchase unit. */
(() => {
  const formatLength = size => String(size).split('×').pop().trim();
  const formatWidth = size => String(size).split('×')[0].trim();
  // The other format opens on the mapped size, so the switch carries it past the open-on-longest rule.
  const sizeQuery = size => {const [w,l] = String(size).match(/\d+(?:\.\d+)?/g) || []; return w && l ? `&width=${w}&length=${l}` : '';};
  const chooseFormatVariant = (format, selected) => {
    const sameColor = format.variants.filter(row => row.color === selected.color);
    const colorRows = sameColor.some(row => row.available) ? sameColor.filter(row => row.available) : sameColor;
    const matchingLength = colorRows.filter(row => formatLength(row.size) === formatLength(selected.size));
    const preferred = format.handle === 'textured-grommet-curtains' ? '52″ W × 84″ L' : '70″ W × 90″ L';
    return matchingLength.find(row => formatWidth(row.size) === formatWidth(selected.size)) || matchingLength.find(row => row.size.startsWith('52″ W')) || matchingLength[0] || colorRows.find(row => row.size === preferred) || colorRows[0] || format.variants.find(row => row.available) || format.variants[0];
  };
  class OnaSourceFormat extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try { this.data = JSON.parse(this.querySelector('[data-source-format-data]').textContent); } catch { return; }
      this.ready = true; this.productInfo = this.closest('product-info');
      this.select = this.querySelector('[data-source-format-select]');
      this.onFormatChange = () => {
        const option = this.select?.selectedOptions[0];
        if (!option || option.value === this.data.current) return;
        const target = option.dataset.sourceFormatUrl;
        if (target) {
          this.select.value = this.data.current;
          window.location.assign(target);
        }
      };
      this.select?.addEventListener('change', this.onFormatChange);
      this.update(this.data.selected);
      if (this.select) this.select.disabled = false;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
        const section = this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section;
        if (event.data?.sectionId === section && event.data.variant) this.update(event.data.variant.id);
      });
    }
    update(id) {
      const current = this.data.formats.find(format => format.handle === this.data.current);
      const selected = current?.variants.find(row => String(row.id) === String(id)); if (!selected) return;
      this.data.formats.forEach(format => {
        const target = format.handle === this.data.current ? selected : chooseFormatVariant(format, selected); if (!target) return;
        const option = this.querySelector(`[data-source-format-option="${format.handle}"]`); if (!option) return;
        option.dataset.sourceFormatUrl = `${format.url}?variant=${target.id}${sizeQuery(target.size)}`;
        option.textContent = option.dataset.sourceFormatLabel;
      });
    }
    disconnectedCallback() { this.unsubscribe?.(); this.select?.removeEventListener('change', this.onFormatChange); this.ready = false; }
  }
  if (!customElements.get('ona-source-format')) customElements.define('ona-source-format', OnaSourceFormat);
})();
